import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { api } from "../lib/api";

const NAV_GROUPS = [
  { label: "Services", href: "/services" },
  { label: "Pool Tips", href: "/pool-tips" },
  { label: "Reviews", href: "/reviews" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenDropdown(false);
  }, [location.pathname]);

  const topCities = cities.filter((c) => c.is_featured);

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="site-header">
      <div className="section-container flex items-center h-32 sm:h-20">
        {/* Mobile-only left spacer balances the right-side menu button so logo centers */}
        <div className="lg:hidden w-10 flex-shrink-0" aria-hidden="true" />

        <Link
          to="/"
          className="flex-1 flex items-center justify-center gap-3 group lg:flex-initial lg:justify-start"
          data-testid="logo-link"
        >
          <img
            src="/poolguru-logo.png"
            alt="The Pool Guru Treasure Coast mascot"
            className="w-28 h-28 object-contain sm:hidden"
          />
          <img
            src="/poolguru-logo.png"
            alt="The Pool Guru Treasure Coast logo"
            className="hidden sm:block w-16 h-16 object-contain"
          />
          <div className="hidden sm:block">
            <div className="font-heading font-extrabold text-brand-navy leading-tight text-lg">
              The Pool Guru
            </div>
            <div className="text-xs font-semibold tracking-widest text-brand-blue uppercase">
              Treasure Coast
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <div
            className="relative"
            onMouseEnter={() => setOpenDropdown(true)}
            onMouseLeave={() => setOpenDropdown(false)}
          >
            <button
              className="flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-brand-pink transition-colors"
              data-testid="nav-locations"
            >
              Locations <ChevronDown className="w-4 h-4" />
            </button>
            {openDropdown && (
              <div className="absolute top-full left-0 pt-3 w-64">
                <div className="bg-white rounded-xl shadow-2xl border border-slate-100 py-3">
                  {topCities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/pool-service-${c.slug}`}
                      className="block px-5 py-2 text-sm text-slate-700 hover:bg-pink-50 hover:text-brand-pink font-medium"
                      data-testid={`nav-city-${c.slug}`}
                    >
                      {c.name}
                    </Link>
                  ))}
                  <div className="border-t my-2" />
                  <Link
                    to="/locations"
                    className="block px-5 py-2 text-sm text-brand-blue font-bold hover:text-brand-blue-hover"
                    data-testid="nav-all-locations"
                  >
                    View all service areas →
                  </Link>
                </div>
              </div>
            )}
          </div>
          {NAV_GROUPS.map((g) => (
            <Link
              key={g.href}
              to={g.href}
              className="text-sm font-semibold text-slate-700 hover:text-brand-pink transition-colors"
              data-testid={`nav-${g.label.toLowerCase()}`}
            >
              {g.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="tel:7722890405"
            className="flex items-center gap-2 text-brand-navy font-bold"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4 text-brand-blue" />
            <span className="hidden xl:inline">772-289-0405</span>
          </a>
          <Link to="/contact" className="btn-primary text-sm" data-testid="header-cta">
            Free Estimate
          </Link>
        </div>

        <button
          className="lg:hidden p-2 w-10 flex-shrink-0"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t bg-white" data-testid="mobile-menu">
          <div className="section-container py-4 space-y-2 max-h-[75vh] overflow-y-auto">
            <div className="text-xs font-bold text-brand-blue uppercase tracking-wider pt-2 pb-1">
              Locations
            </div>
            {topCities.map((c) => (
              <Link
                key={c.slug}
                to={`/pool-service-${c.slug}`}
                className="block py-2 text-sm font-semibold text-slate-700"
                data-testid={`mobile-city-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/locations"
              className="block py-2 text-sm font-bold text-brand-blue"
              data-testid="mobile-all-locations"
            >
              View all service areas →
            </Link>
            <div className="border-t my-3" />
            {NAV_GROUPS.map((g) => (
              <Link
                key={g.href}
                to={g.href}
                className="block py-2 text-sm font-semibold text-slate-700"
                data-testid={`mobile-nav-${g.label.toLowerCase()}`}
              >
                {g.label}
              </Link>
            ))}
            <div className="border-t my-3" />
            <a
              href="tel:7722890405"
              className="flex items-center gap-2 py-2 text-sm font-bold text-brand-navy"
              data-testid="mobile-phone"
            >
              <Phone className="w-4 h-4 text-brand-blue" /> 772-289-0405
            </a>
            <Link to="/contact" className="btn-primary w-full justify-center" data-testid="mobile-cta">
              Free Estimate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
